// @ts-strict-ignore
import { MultiSelect, Tooltip } from '@usealloy/component-library';
import React, {
  useCallback, useContext, useEffect, useMemo, useRef,
} from 'react';

import type {
  DataRoute,
  Expression,
  ExpressionTerm,
  PopulatedDataRoute,
  PendingParenthesisInfo,
  Attribute,
} from 'flow/store/engine/types';
import {
  selectIsSearchBoxVisible,
  selectNodeLineageForAnchorNode,
  selectPendingGroup,
  selectWorkflowData,
  selectWorkflowProps,
} from 'flow/store/redux/selectors';
import {
  addActionOutputExpressionOperatorToOperand,
  addActionOutputExpressionTrailingOperator,
  createActionOutputExpressionItem, createDataRoute,
  groupActionOutputExpressionItem,
  removeActionOutputExpressionItem,
  settleActionOutputExpressionFunctions,
  removeDataRoute,
  resetPendingGroup,
  setPendingGroup,
  replaceActionOutputExpressionItem,
  ungroupActionOutputExpressionItem,
  updateActionOutputExpressionFunction,
  updateActionOutputExpressionItem, updateDataRouteValue,
} from 'flow/store/redux/slice';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import {
  canAddAvailableActionsToDropdown,
  getAvailableActionsAsDropdownOptions,
  getAvailableAttributesAsDropdownOptions,
  getCreatedExpressionItemPath,
  getHasItemsToGroupAfter,
  getMultiSelectProps,
  getPreviousItemInExpressionScope,
} from './helpers';
import {
  createStringOfPathArray,
  getExpressionFunction,
  getIsExpressionFunctionString,
  getIsExpressionGroupAwaitingOperands,
  getIsExpressionGroupWithoutOperators,
  getIsExpressionOperatorString,
} from '~/dashboard/views/workflows/store/engine/utils';
import { generatePublicIdHash } from 'flow/service/publicIds';
import { RuleEditorContext } from '../../NodeEditorDrawer/context';
import type { DropdownOption } from '../../NodeEditorDrawer/types';
import {
  CaretPositionSuffixTypes,
  MLAAttributeItemOrigin,
  RuleEditorDropdownMode,
  RuleEditorDropdownModeCategory,
} from '../../NodeEditorDrawer/types';
import {
  getCaretPositionForItemPath,
  getDropdownModeCategory,
  getFlattenedPathInCaretPositionFormat,
  getIsCaretPositionAtEnd,
  scrubInsertionModeFromCaret,
} from '../../useCaretControl/helpers';
import {
  functionOptions,
  functionSeparatorOption,
  groupingOptions,
  operatorOptions,
} from '../../NodeEditorDrawer/constants';
import { StyledDropdownContainer } from './styles';
import { CaretIndexType, startingCaretPosition } from '../../useCaretControl/constants';
import { canLinkToNode } from '../../Graph/nodes/BaseNode/NodeConnector/helpers';
import { onEscKeyFromRuleDropdown, getSelectedOption } from '../../helpers';
import { StyledSuperset } from '../../mla/styles';
import { checkIfServiceHasMLAAttributes } from '~/dashboard/views/workflows/service/mla';
import { isAggregationServiceAction } from '~/dashboard/views/workflows/service/services';
import { getAllowedAttributesForPortfolioWorkflow } from '~/flow/utils';
import { getProperty } from 'dot-prop';
import type { ExpressionBuilderDropdownOptions } from './types';
import { describeExpressionNode, getIsFunctionContainer } from '../expressionNode';
import { ExpressionInsertionType, resolveExpressionInsertion } from '../expressionInsertion';
import { ExpressionDeletionType, resolveExpressionDeletion } from '../expressionDeletion';
import {
  getEnclosingFunctionGroupPath,
  getExpressionPathInDotNotationFormat,
  getIsFunctionGroupClosing,
} from '../AttributeExpressionGroup/helpers';
import { closeSearchBoxThunk, createWorkflowHistoryThunk } from 'flow/store/redux/thunks';
import { useEditorAccess } from 'flow/editor/context';
import { EditorMode } from 'flow/editor/context/constants';
import { isSnippetChildAction } from 'flow/service/actions/snippets';
import { getNumericPathIndices } from '~/dashboard/views/workflows/service/actions';
import { AGGREGATION_EDIT_OPTION_LABEL } from '../../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/Rule/Threshold/constants';
import { buildAggregationAttributeOptions } from '../../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/Rule/Threshold/helpers';
import { UserContext } from '~/dashboard/context';
import type { WorkflowStateChange } from 'flow/store/redux/types';
import { changeDescription } from 'flow/store/redux/types';
import { createUiEffectContext } from 'flow/service/history';
import { getExpressionHtmlElementId } from 'flow/editor/hooks/useWorkflowHistory/helpers';
import { Operation } from 'flow/service/changelog/types';
import { ExpressionOption } from './ExpressionOption';

function ExpressionBuilderDropdown({
  dataRoute,
  expression,
  expressionPath,
  expressions,
  mode,
  canAddGroupCloseOption = false,
}: {
  dataRoute?: PopulatedDataRoute;
  expression?: ExpressionTerm | Expression;
  expressionPath: string;
  expressions?: Expression;
  mode: RuleEditorDropdownMode;
  canAddGroupCloseOption?: boolean;
}) {
  const dispatch = useAppDispatch();
  const anchorNodeLineage = useAppSelector(selectNodeLineageForAnchorNode);

  const { actions, dataRoutes } = useAppSelector(selectWorkflowData);
  const {
    services, workflow, universalAttributes, aggregationConfig,
  } = useAppSelector(selectWorkflowProps);
  const isSearchBoxVisible = useAppSelector(selectIsSearchBoxVisible);
  const pendingGroup = useAppSelector(selectPendingGroup);
  const { featureFlags } = useContext(UserContext);

  const multiSelectRef = useRef(null);
  const reopenedFunctionRef = useRef(false);

  const {
    morphingCaretPosition,
    setMorphingCaretPosition,
    updateRuleItemRefs,
    getFlattenedCaretPositionIndex,
    setPendingMorphingCaretPositionUpdateValue,
    activeInputValue,
    setActiveInputValue,
    getPreviousCaretPosition,
    getNextCaretPosition,
    isolatedEditingBlock,
    setIsolatedEditingBlock,
    mlaUsedInRules,
    sourceAction,
    ruleEditorFlatCaretIndices,
    ActionHoverTooltip,
  } = useContext(RuleEditorContext);

  const { editorMode } = useEditorAccess();

  const { publicId, output } = sourceAction;

  // a min/max group's opening parenthesis, drawn as "Min (" rather than "("
  const isFunctionOpening = mode === RuleEditorDropdownMode.GROUP_START
    && getIsExpressionFunctionString(expression as string);

  const node = useMemo(
    () => describeExpressionNode({ expressions: output.expression, path: expressionPath }),
    [output.expression, expressionPath],
  );
  const isFunctionArgument = getIsFunctionContainer(node?.container);

  // offered only where a function would be created, so the flag alone decides
  const creatableFunctionOptions = featureFlags?.minMaxOutputAttributes ? functionOptions : [];

  // the closing parenthesis of a min/max group, rather than of an ordinary one
  const isFunctionClosing = mode === RuleEditorDropdownMode.GROUP_END
    && getIsFunctionContainer(node?.group);

  // ungrouping moves the contents into the parent, so a min/max argument waits until it has no
  // operator left
  const groupAtPath = getProperty(output.expression, expressionPath);
  const canUngroup = !isFunctionArgument || getIsExpressionGroupWithoutOperators(groupAtPath);

  // "insertion" means we're adding an item *between* existing ones (a morphing
  // caret dropdown sits between items) rather than appending at the end. When
  // there's no expressionPath, the reducers/helper ignore this and append.
  const insertion = mode === RuleEditorDropdownMode.CARET_EXPRESSION;

  const makeExpressionUiEffectContext = useCallback((
    operation: Operation,
    path: string,
  ) => (
    operation === Operation.Add
      // the expression path will be for the next index, not the one at this dropdown
      ? createUiEffectContext(changeDescription.expression, {
        expressionPath: getCreatedExpressionItemPath({
          existingExpression: output.expression,
          path,
          insertion,
        }),
        actionId: sourceAction.publicId,
      })
      : createUiEffectContext(changeDescription.expression, {
        expressionPath,
        actionId: sourceAction.publicId,
      })
  ), [expressionPath, insertion, output.expression, sourceAction.publicId]);

  const handleDispatch = useCallback((
    operation: Operation,
    stateChange: WorkflowStateChange,
  ) => {
    dispatch(createWorkflowHistoryThunk(
      stateChange,
      Boolean(featureFlags?.workflowEditorUndoRedo),
      {
        operation,
        uiEffectContext: makeExpressionUiEffectContext(operation, expressionPath),
      },
    ));
  }, [
    dispatch,
    expressionPath,
    featureFlags?.workflowEditorUndoRedo,
    makeExpressionUiEffectContext,
  ]);

  // is the input manually typed in / entered?
  const isInputTypedIn = activeInputValue.value.length > 0;

  // flattened path meaning we convert expression path (e.g. [0][1]..) into 0-1 - which is the format we use for caret position / caret indices
  const flatCaretPosition = useMemo(() => getFlattenedCaretPositionIndex({
    itemPath: expressionPath,
    mode,
    type: CaretIndexType.EXPRESSION,
  }), [expressionPath, getFlattenedCaretPositionIndex, mode]);

  useEffect(() => {
    if (multiSelectRef?.current && flatCaretPosition) {
      updateRuleItemRefs({
        path: flatCaretPosition,
        width: multiSelectRef.current.controlRef?.offsetWidth,
      });
    }
  }, [flatCaretPosition, multiSelectRef, updateRuleItemRefs]);

  const canAddGroupOpenOption = useMemo(() => pendingGroup.path === null
    && !getNextCaretPosition({}).newPositionString?.endsWith(CaretPositionSuffixTypes.GROUP_END),
  [pendingGroup.path, getNextCaretPosition]);

  const options = React.useMemo<ExpressionBuilderDropdownOptions>(() => {
    let array: DropdownOption[] = [];
    const combinations = { userDefinedValue: null, operator: false };

    const outgoers = new Set([
      ...anchorNodeLineage.directOutgoers,
      ...anchorNodeLineage.extendedOutgoers,
    ]);
    const allAvailableActions = getAvailableActionsAsDropdownOptions(actions, publicId, outgoers);

    const processExpression = () => {
      switch (typeof expression) {
        case 'undefined': {
          // new expression case
          const previousItemInExpressionsScope = getPreviousItemInExpressionScope({
            expressions, expressionPath, mode, output,
          });

          if (canAddGroupOpenOption) array.push(groupingOptions.open);
          else if (canAddGroupCloseOption) array.push(groupingOptions.close);

          if (canAddAvailableActionsToDropdown(expressions, previousItemInExpressionsScope)) {
            array.push(...creatableFunctionOptions, ...allAvailableActions);
          } else {
            array.push(...operatorOptions);
            combinations.operator = true;

            /*
              Reopens the function for another argument. Only the group directly around this position counts:
              an ordinary group sitting inside a function has no argument list to extend.
            */
            if (isFunctionArgument) {
              array.unshift(functionSeparatorOption);
            }
          }
          break;
        }
        case 'string': {
          if (getIsExpressionFunctionString(expression)) {
            // Leaving `combinations` unset stops a typed arithmetic operator replacing min/max on blur.
            array = functionOptions;
          } else if (getIsExpressionOperatorString(expression)) {
            // operator string
            array = operatorOptions;
            combinations.operator = true;
          } else {
            // non operator string
            array.unshift({
              value: String(expression),
              label: expression,
              meta: { category: 'string' },
            });
            combinations.userDefinedValue = 'string';
          }
          break;
        }
        case 'number': {
          // expression exists; so this is edit mode
          array = [
            {
              value: String(expression),
              label: String(expression),
              meta: { category: 'decimal' },
            },
            ...allAvailableActions,
          ];
          combinations.userDefinedValue = 'decimal';
          combinations.operator = true;
          break;
        }
        default: {
          // do nothing
        }
      }
    };

    const processDataRouteAttribute = () => {
      // the dropdown is for configuring data route attribute
      /*
        Temporarily hiding features for Portfolio Evaluations
        Epic ID: 109173
      */

      const allowedServiceAttributes: {
        [attribute_id: number]: Attribute;
      } = getAllowedAttributesForPortfolioWorkflow(
        workflow.type,
        services[dataRoute?.attribute?.service.internal_name]?.name,
        dataRoute?.attribute?.service.attributes,
      );
      const allowedAttributes = {
        ...allowedServiceAttributes,
        ...universalAttributes,
      };
      const allAvailableAttributesOfSelectedAction = getAvailableAttributesAsDropdownOptions(
        allowedAttributes,
      );

      const isMLA = checkIfServiceHasMLAAttributes(dataRoute.attribute.service.internal_name);
      if (isMLA) {
        const otherAttributes = allAvailableAttributesOfSelectedAction.filter(
          attribute => attribute.value !== String(dataRoute?.attribute.id),
        );
        const isCurrentAttrMLA = dataRoute?.attribute?.multiLevelAttributeFilter
          ?.filterComponents?.length > 0;
        array = [
          {
            label: 'Other Multi-Level Attributes',
            value: undefined,
            options: otherAttributes.filter(
              attribute => attribute.label.includes('Multi-Level') || attribute.meta?.isMLA,
            ),
          },
          {
            label: 'All Other Attributes',
            value: undefined,
            options: allAvailableAttributesOfSelectedAction.filter(
              attribute => !attribute.label.includes('Multi-Level') && !attribute.meta?.isMLA,
            ),
          },
        ];

        if (isCurrentAttrMLA) {
          array.unshift({
            label: 'Edit Attribute',
            value: String(dataRoute?.attribute.id),
            meta: {
              shortenedLabel: dataRoute?.attribute.displayName,
              category: 'attribute',
            },
          });
        }
      } else {
        array = allAvailableAttributesOfSelectedAction;
      }

      const aggregationComponent = dataRoute?.aggregationComponents?.[0];
      if (isAggregationServiceAction(dataRoute.sourceAction)) {
        array = buildAggregationAttributeOptions(
          array,
          aggregationComponent?.displayName || dataRoute.attribute.displayName,
          dataRoute.attribute.id,
          aggregationConfig,
        );
      }
    };

    switch (mode) {
      case RuleEditorDropdownMode.EXPRESSION:
      case RuleEditorDropdownMode.CARET_EXPRESSION: {
        processExpression();
        break;
      }
      case RuleEditorDropdownMode.DATAROUTE_ATTRIBUTE: {
        processDataRouteAttribute();
        break;
      }
      case RuleEditorDropdownMode.DATAROUTE_SOURCE: {
        // the dropdown is for configuring data route source
        array = [
          ...creatableFunctionOptions,
          ...allAvailableActions.filter(
            action => canLinkToNode(anchorNodeLineage, action.value),
          ),
        ];
        break;
      }
      case RuleEditorDropdownMode.GROUP_END: {
        // a min/max group offers Delete Group instead of Ungroup, because ungrouping would leave its
        // operators inside an argument list. An ordinary group still ungroups.
        if (isFunctionClosing) {
          array = [groupingOptions.end, groupingOptions.deleteGroup];
        } else {
          array = canUngroup
            ? [groupingOptions.end, groupingOptions.ungroup]
            : [groupingOptions.end];
        }
        break;
      }
      case RuleEditorDropdownMode.GROUP_START: {
        /*
          On a min/max opening, any choice except the other function replaces the whole group. Not
          flag-gated, so a group saved while the flag was on can still be switched or replaced with
          the flag off.
        */
        array = isFunctionOpening
          ? [groupingOptions.open, ...functionOptions, ...allAvailableActions]
          : [groupingOptions.start, ...(canUngroup ? [groupingOptions.ungroup] : [])];
        break;
      }
      case RuleEditorDropdownMode.GROUP_START_PENDING: {
        array = [groupingOptions.start, groupingOptions.cancel];
        break;
      }
      default: {
        // do nothing
      }
    }

    return {
      array,
      combinations,
    };
  }, [
    actions, mode, publicId, output, canAddGroupCloseOption, canAddGroupOpenOption,
    expression, expressions, expressionPath, dataRoute, isFunctionOpening, isFunctionClosing,
    canUngroup, isFunctionArgument, creatableFunctionOptions,
    anchorNodeLineage, services, workflow, universalAttributes, aggregationConfig,
  ]);

  const selectedOption = useMemo(() => {
    const modeMap = {
      [RuleEditorDropdownMode.DATAROUTE_SOURCE]: dataRoute?.sourceAction?.publicId,
      [RuleEditorDropdownMode.DATAROUTE_ATTRIBUTE]: dataRoute?.attribute?.id,
      [RuleEditorDropdownMode.EXPRESSION]: (expression === null || expression === undefined)
        ? null
        : expression,
      // on a min/max opening, `expression` is the group's operator, so the token reads "Min"
      [RuleEditorDropdownMode.GROUP_START]: isFunctionOpening ? expression : '(',
      [RuleEditorDropdownMode.GROUP_START_PENDING]: '(',
      [RuleEditorDropdownMode.GROUP_END]: ')',
    };

    // null, not undefined: MultiSelect treats undefined as uncontrolled and keeps showing the last
    // option picked, even after the expression no longer holds it
    return getSelectedOption({
      incomingValue: String(modeMap[mode]),
      options: options.array,
    }) ?? null;
  }, [mode, options, dataRoute, expression, isFunctionOpening]);

  const onChange = useCallback(inputValue => {
    const { value, meta } = inputValue;
    // typecast as a number instead of string when necessary
    const parsedValue = isNaN(Number(value)) ? value : Number(value);

    /*
      An opening parenthesis is normally closed later, around the items that follow it. With nothing
      following, there is nothing to close around, so create an empty pair instead.
    */
    const hasItemsToGroupAfter = Boolean(expressions.length)
      && !getIsCaretPositionAtEnd(ruleEditorFlatCaretIndices, morphingCaretPosition)
      && getHasItemsToGroupAfter({ expressions, expressionPath });

    const { type } = resolveExpressionInsertion({
      node,
      mode,
      category: meta?.category,
      value: String(value),
      isFunctionOpening,
      hasItem: Boolean(expression),
      hasItemsToGroupAfter,
    });

    /*
      The comma reopens the function instead of adding a term, so the caret does not move: putting the
      trailing operator back leaves every index unchanged.
    */
    if (type === ExpressionInsertionType.REOPEN_FUNCTION) {
      const groupPath = getEnclosingFunctionGroupPath({
        expressions: output.expression,
        position: expressionPath,
      });

      if (groupPath) {
        handleDispatch(
          Operation.Modify,
          () => dispatch(addActionOutputExpressionTrailingOperator({
            id: publicId,
            expressionPath: groupPath,
          })),
        );

        // the caret has not moved, so the effect that focuses on a position change will not run
        reopenedFunctionRef.current = true;
        multiSelectRef.current?.focus();
        multiSelectRef.current?.openMenu('first');
      }
      return;
    }

    /*
      Wrapped together with the operand, so Min ( a , b ) becomes Min ( a , ( b * ) ). Placed directly in
      the group, the operator would apply to all the arguments and the group would stop being a function.
    */
    if (type === ExpressionInsertionType.WRAP_OPERAND) {
      handleDispatch(
        Operation.Modify,
        () => dispatch(addActionOutputExpressionOperatorToOperand({
          id: publicId,
          expressionPath,
          value: String(value),
        })),
      );

      // the advance below reads indices this dispatch replaces, so it cannot find the new position
      const operatorPath = getFlattenedPathInCaretPositionFormat(`${expressionPath}[1]`);
      setMorphingCaretPosition(`${operatorPath}${CaretPositionSuffixTypes.CARET_INSERTION}`);
      return;
    }

    const handleFunctionStart = () => {
      if (type === ExpressionInsertionType.SWITCH_FUNCTION) {
        handleDispatch(
          Operation.Modify,
          () => dispatch(updateActionOutputExpressionFunction({
            id: publicId,
            expressionPath,
            value: parsedValue,
          })),
        );
        return;
      }

      const newDataRoutePublicId = meta?.category === 'action'
        ? `dataroute__${generatePublicIdHash(20)}`
        : undefined;

      handleDispatch(
        Operation.Modify,
        () => dispatch(replaceActionOutputExpressionItem({
          id: publicId,
          expressionPath,
          value: meta?.category === 'grouping' ? [] : parsedValue,
          newDataRoute: newDataRoutePublicId
            ? {
              publicId: newDataRoutePublicId,
              sourceId: value,
              targetId: publicId,
            } as DataRoute
            : undefined,
        })),
      );
    };

    const handleNewExpression = () => {
      switch (type) {
        case ExpressionInsertionType.OPEN_FUNCTION: {
          // the operator on its own draws "Min ( )", and the arguments fill in around it
          handleDispatch(
            Operation.Add,
            () => dispatch(createActionOutputExpressionItem({
              id: publicId,
              path: expressionPath,
              value: [parsedValue],
              insertion,
            })),
          );
          break;
        }
        case ExpressionInsertionType.CREATE_DATA_ROUTE: {
          // user clicked a service in the dropdown, so we create data route.
          // when creating data route, that reducer will also update action expression.
          const newDataRoutePublicId = `dataroute__${generatePublicIdHash(20)}`;
          handleDispatch(
            Operation.Add,
            () => dispatch(createDataRoute({
              path: newDataRoutePublicId,
              value: {
                publicId: newDataRoutePublicId,
                sourceId: value,
                targetId: publicId,
              },
              expressionPath,
              // if insertion is true, we "insert," not add at the end of the expression array
              insertion,
            })),
          );

          // Reset the pending paranthesis when a new expression item is added
          // as adding/removing expression items can make the pending group index stale
          if (pendingGroup.path) {
            dispatch(resetPendingGroup());
          }
          break;
        }
        case ExpressionInsertionType.CREATE_GROUP: {
          handleDispatch(
            Operation.Add,
            () => dispatch(createActionOutputExpressionItem({
              id: publicId,
              path: expressionPath,
              value: [],
              insertion,
            })),
          );
          break;
        }
        case ExpressionInsertionType.PEND_GROUP: {
          const prevPosition = getPreviousCaretPosition({}).newPositionString || '';

          // in these cases, we need to start the new grouping before the next expression item
          const isFirstDropdown = morphingCaretPosition === startingCaretPosition;
          const isStartedAfterGrouping = (
            prevPosition.endsWith(CaretPositionSuffixTypes.GROUP_END)
            || prevPosition.endsWith(CaretPositionSuffixTypes.GROUP_START)
          );

          const pendingParanthesisInfo: PendingParenthesisInfo = (
            isFirstDropdown || isStartedAfterGrouping
          ) ? {
              path: getExpressionPathInDotNotationFormat({
                expressions,
                itemFlatCaret: getNextCaretPosition({}).newPositionString,
                workflowInfo: {
                  actions, dataRoutes, services, universalAttributes,
                },
              }).path,
              side: 'before',
            }
            : { path: expressionPath, side: 'after' };

          dispatch(setPendingGroup(pendingParanthesisInfo));
          break;
        }
        case ExpressionInsertionType.CLOSE_GROUP: {
          handleDispatch(
            Operation.Add,
            () => dispatch(groupActionOutputExpressionItem({
              id: publicId,
              groupOpeningInfo: pendingGroup,
              groupClosingPath: expressionPath,
            })),
          );
          dispatch(resetPendingGroup());
          break;
        }
        default: {
          // if expression path exists, we use that.
          // insertion mode will be set when expression path and mode meets the criteria;
          // this means we just add a new expression item at the end of the expression array
          handleDispatch(
            Operation.Add,
            () => dispatch(createActionOutputExpressionItem({
              id: publicId,
              path: expressionPath,
              value: parsedValue,
              insertion,
            })),
          );
        }
      }
    };

    const isFunctionStart = type === ExpressionInsertionType.SWITCH_FUNCTION
      || type === ExpressionInsertionType.REPLACE_GROUP;
    const isUpdate = type === ExpressionInsertionType.UPDATE_FUNCTION
      || type === ExpressionInsertionType.UPDATE_ITEM;

    if (isFunctionStart) {
      handleFunctionStart();
    } else if (isUpdate) {
      // an item with no path of its own has nothing to update
      if (expressionPath) {
        handleDispatch(
          Operation.Modify,
          () => dispatch(type === ExpressionInsertionType.UPDATE_FUNCTION
            ? updateActionOutputExpressionFunction({
              id: publicId,
              expressionPath,
              value: parsedValue,
            })
            : updateActionOutputExpressionItem({
              id: publicId,
              expressionPath,
              value: parsedValue,
            })),
        );
      }
    } else if (type !== ExpressionInsertionType.NONE) {
      handleNewExpression();
    } else if (meta) {
      switch (meta.category) {
        case 'function': {
          // the operand becomes an empty function, e.g. a * b with a replaced reads Min ( ) * b
          if (mode === RuleEditorDropdownMode.DATAROUTE_SOURCE) {
            handleDispatch(
              Operation.Modify,
              () => dispatch(replaceActionOutputExpressionItem({
                id: publicId,
                expressionPath,
                value: [parsedValue],
              })),
            );
          }
          break;
        }
        case 'action': {
          if (dataRoute?.sourceAction && mode === RuleEditorDropdownMode.DATAROUTE_SOURCE) {
            handleDispatch(
              Operation.Modify,
              () => dispatch(updateDataRouteValue(
                { path: `${dataRoute.publicId}.sourceId`, value },
              )),
            );
          }
          break;
        }
        case 'attribute': {
          if (dataRoute?.sourceAction && mode === RuleEditorDropdownMode.DATAROUTE_ATTRIBUTE) {
            if (dataRoute?.attribute?.multiLevelAttributeFilter?.filterComponents?.length > 0
              && inputValue.label === 'Edit Attribute') {
              setIsolatedEditingBlock({
                originPublicId: dataRoute.publicId,
                originType: MLAAttributeItemOrigin.DATAROUTE,
                originCaretIndex: flatCaretPosition,
              });
            } else if (inputValue.label === AGGREGATION_EDIT_OPTION_LABEL) {
              setIsolatedEditingBlock({
                originPublicId: dataRoute.publicId,
                originType: MLAAttributeItemOrigin.DATAROUTE,
                originCaretIndex: flatCaretPosition,
              });
            } else {
              handleDispatch(
                Operation.Modify,
                () => dispatch(updateDataRouteValue(
                  { path: `${dataRoute.publicId}.attribute.id`, value: parsedValue },
                )),
              );
            }
          }
          break;
        }
        case 'grouping': {
          if (value === groupingOptions.ungroup.value) {
            handleDispatch(
              Operation.Modify,
              () => dispatch(ungroupActionOutputExpressionItem({ id: publicId, expressionPath })),
            );
          } else if (value === groupingOptions.deleteGroup.value) {
            /*
              Every position the group drew goes with it, so the caret has to be aimed at the item
              in front of it, read now while that item's position is still the one on screen. A
              group at the very front leaves nothing in front of it, so the caret goes to the start.
            */
            const indices = getNumericPathIndices(expressionPath);
            const indexBeforeGroup = Number(indices.at(-1)) - 1;
            const caretAfterDelete = indexBeforeGroup >= 0
              ? getCaretPositionForItemPath(
                ruleEditorFlatCaretIndices,
                createStringOfPathArray([...indices.slice(0, -1), String(indexBeforeGroup)]),
              )
              : undefined;

            // expressionPath is the group's own path, so this takes the whole function with it
            handleDispatch(
              Operation.Remove,
              () => dispatch(removeActionOutputExpressionItem({ id: publicId, expressionPath })),
            );

            setMorphingCaretPosition(caretAfterDelete
              ? `${caretAfterDelete}${CaretPositionSuffixTypes.CARET_INSERTION}`
              : startingCaretPosition);

            return;
          } else if (value === groupingOptions.cancel.value) {
            dispatch(resetPendingGroup());
          }
          break;
        }
        default: {
          // do nothing
        }
      }
    }

    // we update caret position accordingly because user changed something, and expect the cursor to be moving to the next.
    // but if the mode was CARET_EXPRESSION, meaning it's gonna insert an item somewhere in between, it may result in index changes.
    // so we store the index of where the cursor should be to pending update state in the context and let it update once after indices are regenerated.
    const { newPositionString, newPositionIndexForDelayedUpdate } = getNextCaretPosition({
      userActionObject: {
        meta: meta?.category,
        value,
        mode,
      },
    });

    if (newPositionIndexForDelayedUpdate) {
      setPendingMorphingCaretPositionUpdateValue(newPositionIndexForDelayedUpdate);
    } else {
      setMorphingCaretPosition(newPositionString);
    }
  }, [
    actions, dataRoutes, services, universalAttributes,
    mode, dataRoute,
    expression, expressionPath,
    dispatch, publicId,
    setPendingMorphingCaretPositionUpdateValue,
    setMorphingCaretPosition,
    getNextCaretPosition,
    flatCaretPosition,
    setIsolatedEditingBlock,
    expressions,
    morphingCaretPosition,
    ruleEditorFlatCaretIndices,
    pendingGroup,
    getPreviousCaretPosition,
    handleDispatch,
    isFunctionOpening,
    insertion,
    node,
  ]);

  const selectProps = useMemo(() => getMultiSelectProps(
    dataRoute?.sourceAction,
    mode,
    mode === RuleEditorDropdownMode.GROUP_START_PENDING,
    isFunctionOpening,
  ), [dataRoute?.sourceAction, mode, isFunctionOpening]);

  const updateValueUsingActiveInputValue = (inputValue: string) => {
    /*
      min and max count as operator strings, so typing one would leave a bare operator in the
      expression. Picking from the dropdown wraps it in a group instead, which is the only shape the
      editor draws, so typing is never a way to make one.
    */
    const isTypedFunction = getIsExpressionFunctionString(inputValue);
    const isOperatorString = getIsExpressionOperatorString(inputValue) && !isTypedFunction;
    const isNumberValue = !isNaN(Number(inputValue));
    const availableOptionsContainOperatorOrUserDefinedString = options?.combinations.operator || options?.combinations.userDefinedValue === 'string';
    const availableOptionsContainActionsOrUserDefinedNumber = options?.combinations.operator || options?.combinations.userDefinedValue === 'string';
    if (
      (availableOptionsContainOperatorOrUserDefinedString && isOperatorString)
      || (availableOptionsContainActionsOrUserDefinedNumber && isNumberValue)
    ) {
      onChange({
        label: inputValue,
        value: inputValue,
        meta: {
          category: isOperatorString ? 'operator' : '',
          textInput: true,
        },
      });
      setActiveInputValue({
        value: '',
        path: '',
        initialValue: false,
      });
    }
  };

  const onInputChange = (inputValue: string) => {
    // we track this to handle dropdown delete key down handling
    setActiveInputValue({
      value: inputValue,
      path: expressionPath,
      initialValue: false,
    });

    if (inputValue.endsWith(' ')) {
      // whitespace at the end, we'll try see if this is just number/operator with a whitespace at the end
      // if so, we'll automatically "submit" this for user convenience
      const trimmedInputValue = inputValue.slice(0, inputValue.length - 1);
      updateValueUsingActiveInputValue(trimmedInputValue);
    }
  };

  const onDeleteItem = (inputValue: string) => {
    // if activeInputValue has something, user should be able to delete indiv character in the dropdown box, not delete the whole item
    if (isInputTypedIn) return;
    // while text is being typed, backspace clears the text rather than deleting the item
    if (mode === RuleEditorDropdownMode.EXPRESSION && inputValue?.length > 0) return;

    // backspace can take an argument just past a min/max group's ) , or inside one until only its
    // operator is left
    const caretPosition = scrubInsertionModeFromCaret(morphingCaretPosition || '');
    const caretGroup = getProperty(output.expression, expressionPath);
    const isCaretPastFunction = getIsFunctionGroupClosing({
      expressions: output.expression,
      position: caretPosition,
    }) || (
      caretPosition.endsWith(CaretPositionSuffixTypes.GROUP_START)
      && getIsExpressionGroupAwaitingOperands(caretGroup)
      && Boolean(getExpressionFunction(caretGroup))
    );

    const deletion = resolveExpressionDeletion({
      node: describeExpressionNode({ expressions: output.expression, path: expressionPath }),
      mode,
      isCaretPastFunction,
      hasDataRoute: getDropdownModeCategory(mode) === RuleEditorDropdownModeCategory.DATAROUTE,
    });

    // read before the dispatch, since the removed item sits after anything the caret can land on
    const caretAfterDeletion = deletion.caretPath
      ? getCaretPositionForItemPath(ruleEditorFlatCaretIndices, deletion.caretPath)
      : deletion.caretInsideGroupPath && `${getFlattenedPathInCaretPositionFormat(
        deletion.caretInsideGroupPath)}${CaretPositionSuffixTypes.GROUP_START}`;

    const buildActionByType = {
      [ExpressionDeletionType.DATA_ROUTE]: () => removeDataRoute({
        id: dataRoute?.publicId,
        allowEmptyParentGroup: true,
      }),
      [ExpressionDeletionType.UNGROUP]: () => ungroupActionOutputExpressionItem({
        id: publicId,
        expressionPath: deletion.path,
      }),
    };
    if (deletion.type !== ExpressionDeletionType.NONE) {
      const buildAction = buildActionByType[deletion.type] ?? (
        () => removeActionOutputExpressionItem({ id: publicId, expressionPath: deletion.path })
      );

      handleDispatch(Operation.Remove, () => dispatch(buildAction()));

      if (deletion.type === ExpressionDeletionType.DATA_ROUTE && pendingGroup.path) {
        dispatch(resetPendingGroup());
      }

      if (deletion.type === ExpressionDeletionType.ITEM
        || deletion.type === ExpressionDeletionType.DATA_ROUTE) {
        setActiveInputValue({ value: '', path: '', initialValue: false });
      }

      if (caretAfterDeletion) {
        setMorphingCaretPosition(`${caretAfterDeletion}${CaretPositionSuffixTypes.CARET_INSERTION}`);
        return;
      }

      // the group keeps the parenthesis the caret is on, so pressing again takes the next item
      if (deletion.type === ExpressionDeletionType.LAST_ITEM) return;
    }

    // a caret holds nothing to take, so backspace steps back onto the item the next press deletes

    // we update caret position accordingly because user deleted an expression item, and expect the cursor to be moving to the item left to whatever that's now deleted.
    // but things are being deleted and it may result in index changes, especially when group parenthesis is removed. (caret indices are regenerated)
    // so we store the index of where the cursor should be to pending update state in the context and let it update once after indices are regenerated.
    const { newPositionString, newPositionIndexForDelayedUpdate } = getPreviousCaretPosition({
      userActionObject: {
        mode: 'deletion',
      },
    });

    if (newPositionIndexForDelayedUpdate) {
      setPendingMorphingCaretPositionUpdateValue(newPositionIndexForDelayedUpdate);
    } else {
      setMorphingCaretPosition(newPositionString);
    }
  };

  useEffect(() => {
    if (morphingCaretPosition === flatCaretPosition
      || mode === RuleEditorDropdownMode.CARET_EXPRESSION) {
      // focus the dropdown automatically when caret position is pointing here
      multiSelectRef?.current?.focus();
    }
  }, [flatCaretPosition, mode, morphingCaretPosition]);

  const onFocus = () => {
    if (isSearchBoxVisible) {
      dispatch(closeSearchBoxThunk());
    }
    if (morphingCaretPosition !== flatCaretPosition && flatCaretPosition) {
      // update caret position to be here if focused by other ways (mouse click etc)
      setMorphingCaretPosition(`${flatCaretPosition}`);
    }
  };

  const onAltKey = () => setActiveInputValue({
    value: selectedOption?.value || '',
    path: expressionPath,
    initialValue: true,
  });

  const onBlur = () => {
    // if there's any value provided by user, we'll automatically "submit" this for user convenience
    if (isInputTypedIn) updateValueUsingActiveInputValue(activeInputValue.value);

    // picking the comma blurs the input on its way to reopening the menu, so this is not really
    // leaving the field
    if (reopenedFunctionRef.current) {
      reopenedFunctionRef.current = false;
      return;
    }

    /*
      Focus has left, so close every function. Leaving none open is deliberate: clicking away does not
      move the caret, so the group it still points at would stay open forever.
    */
    handleDispatch(
      Operation.Modify,
      () => dispatch(settleActionOutputExpressionFunctions({ id: publicId })),
    );
  };

  const stringInputValue = isInputTypedIn
    ? activeInputValue.value
    : selectedOption?.value || '';

  const inputValue = (
    mode === RuleEditorDropdownMode.EXPRESSION
    && activeInputValue.path === expressionPath
    && morphingCaretPosition === flatCaretPosition
  )
    ? stringInputValue
    : undefined;

  const mlaEditorEnabledForAnotherItem = (
    isolatedEditingBlock.originCaretIndex
    && isolatedEditingBlock.originCaretIndex !== flatCaretPosition
  );

  const onEscKey = (_: string, e: React.KeyboardEvent<HTMLInputElement>) => {
    onEscKeyFromRuleDropdown(
      e,
      activeInputValue,
      setActiveInputValue,
      multiSelectRef,
    );
  };

  const attrMLAFootnoteNumber = useMemo(() => (
    dataRoute && mode === RuleEditorDropdownMode.DATAROUTE_ATTRIBUTE
      ? mlaUsedInRules.findIndex(rule => (
        rule.publicId === dataRoute.publicId
        && rule.type === 'dataRoute'
      ))
      : -1
  ), [dataRoute, mode, mlaUsedInRules]);

  // makes case insensitive and ':' agnostic
  const cleanseString = (str: string) => str.replace(':', '').toLowerCase();

  // helper func to filters options
  const includesActiveInput = (option, inputtedValue: string) => (
    cleanseString(option.label).includes(cleanseString(inputtedValue))
  );

  const isDropdownDisabled = Boolean(isolatedEditingBlock.originPublicId)
    || (isSnippetChildAction(sourceAction))
    || ![EditorMode.CheckedOut, EditorMode.Standard]
      .includes(editorMode);

  const tooltipContent = useMemo(() => {
    if (ActionHoverTooltip && [
      RuleEditorDropdownMode.DATAROUTE_SOURCE,
    ].includes(mode) && !Array.isArray(selectedOption) && selectedOption?.value) {
      const hoveredAction = actions[selectedOption.value];
      return (
        <ActionHoverTooltip action={hoveredAction} />
      );
    }

    return null;
  }, [ActionHoverTooltip, actions, mode, selectedOption]);

  return (
    <Tooltip
      align="start"
      content={tooltipContent}
      side="top"
      variant="secondary"
      hide={(
        !tooltipContent
        || (
          selectedOption
          && !Array.isArray(selectedOption)
          && !selectedOption?.meta?.shortenedLabel
        )
        || !selectedOption
        || (Array.isArray(selectedOption) && !selectedOption.length)
      )}
    >
      <StyledDropdownContainer
        id={getExpressionHtmlElementId(expressionPath)}
        isDimmed={mlaEditorEnabledForAnotherItem}
        isTemporary={mode === RuleEditorDropdownMode.GROUP_START_PENDING}
        tabIndex={-1}
      >
        <MultiSelect
          {...selectProps}
          openMenuOnFocus
          onChange={onChange}
          options={options?.array}
          onFocus={onFocus}
          onBlur={onBlur}
          inputValue={inputValue}
          selectedOptions={selectedOption}
          isCreatable
          isDisabled={isDropdownDisabled}
          placeholder={mode === RuleEditorDropdownMode.CARET_EXPRESSION ? '' : '...'}
          createOptionPosition="first"
          customMenuPortalTarget={document.querySelector('.dashboard-view')}
          onCreateOption={
            input => onChange({ ...input, meta: { ...input?.meta, textInput: true } })
          }
          onKeyDownHandlers={{
            Backspace: onDeleteItem,
            Delete: onDeleteItem,
            Alt: mode === RuleEditorDropdownMode.EXPRESSION ? onAltKey : undefined,
            Escape: onEscKey,
          }}
          onInputChange={onInputChange}
          filterOption={isInputTypedIn ? includesActiveInput : undefined}
          formatCreateLabel={newValue => (`Add ${!isNaN(Number(newValue)) ? 'number:' : 'string:'} ${newValue}`)}
          ref={multiSelectRef}
          components={{ Option: ExpressionOption }}
        />
        {attrMLAFootnoteNumber > -1 ? (
          <StyledSuperset>{attrMLAFootnoteNumber + 1}</StyledSuperset>
        ) : null}
      </StyledDropdownContainer>
    </Tooltip>
  );
}

export default ExpressionBuilderDropdown;
